import { resolveAuthUserDocument } from './authUserIdentity';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';

export interface EligibilityResult {
  eligible: boolean;
  reason?: string;
  previousEditions?: string[];
}

/**
 * Check if a user is eligible for pre-registration based on previous participation
 * in the same event (same eventId).
 * 
 * @param userId - The user's Firebase Auth UID
 * @param eventId - The event ID (e.g., 'kutc', 'mo')
 * @param currentEditionId - The current edition ID to exclude from search
 * @returns EligibilityResult with eligible status and reason
 */
export async function checkPreRegistrationEligibility(
  userId: string,
  eventId: string,
  currentEditionId: string
): Promise<EligibilityResult> {
  try {
    console.log('[Eligibility Check] Starting check for:', { userId, eventId, currentEditionId });
    
    const account = await resolveAuthUserDocument(db, userId);
    if (!account) return { eligible: false, reason: 'Account profile is not ready' };
    const accountUserId = account.id;
    const canonicalPersonId = account.data()?.personId ?? null;

    const previousEditions: string[] = [];
    const debugInfo: any[] = [];
    
    // Determine which results collection to check based on eventId
    const normalizedEventId = String(eventId || '').toLowerCase();
    let resultsCollectionName: string | null = null;
    
    if (normalizedEventId === 'kutc' || normalizedEventId.startsWith('kutc-')) {
      resultsCollectionName = 'kutcResults';
    } else if (normalizedEventId === 'mo' || normalizedEventId.startsWith('mo-')) {
      resultsCollectionName = 'moResults';
    }
    
    console.log('[Eligibility Check] Results collection to check:', resultsCollectionName);
    
    // 1. Check results collection for previous participations (if applicable)
    if (resultsCollectionName) {
      try {
        const resultsCollectionRef = collection(db, resultsCollectionName);
        const editionsSnapshot = await getDocs(resultsCollectionRef);
        
        console.log('[Eligibility Check] Found editions in results:', editionsSnapshot.size);
        
        // Get user's personId from users collection
        const personId: number | null = canonicalPersonId;

        console.log('[Eligibility Check] User personId:', personId);
        
        if (personId) {
          for (const editionDoc of editionsSnapshot.docs) {
            const editionId = editionDoc.id;
            
            // Skip current edition
            if (editionId === currentEditionId) {
              console.log('[Eligibility Check] Skipping current edition in results:', editionId);
              continue;
            }
            
            // Check if user has results in this edition
            // Try total results first
            const totalResultRef = doc(db, `${resultsCollectionName}/${editionId}/races/total/results/${personId}`);
            const totalResultSnap = await getDoc(totalResultRef);
            
            if (totalResultSnap.exists()) {
              const resultData = totalResultSnap.data();
              const editionData = editionDoc.data();
              const year = editionData?.year || editionId.match(/\d{4}/)?.[0] || '';
              const eventName = editionData?.eventName || editionData?.name || '';
              
              previousEditions.push(`${eventName} ${year}`.trim());
              
              debugInfo.push({
                editionId,
                source: 'results',
                personId,
                year,
                status: resultData.status
              });
              
              console.log('[Eligibility Check] ✓ Found result in edition:', editionId);
            }
          }
        }
      } catch (error) {
        console.error('[Eligibility Check] Error checking results collection:', error);
      }
    }
    
    // 2. Also check registrations collection for recent events
    const registrationsQuery = query(
      collection(db, 'registrations'),
      where('userId', '==', accountUserId)
    );
    
    const registrationsSnap = await getDocs(registrationsQuery);
    
    console.log('[Eligibility Check] Found registrations:', registrationsSnap.size);
    
    for (const regDoc of registrationsSnap.docs) {
      const registration = regDoc.data();
      if (registration.personId != null && registration.personId !== canonicalPersonId)
        throw new Error('Registration identity conflicts with its linked account.');
      const editionId = registration.editionId;
      
      // Skip current edition
      if (editionId === currentEditionId) {
        console.log('[Eligibility Check] Skipping current edition:', editionId);
        continue;
      }
      
      // Skip if no editionId
      if (!editionId) {
        console.log('[Eligibility Check] Skipping registration without editionId');
        continue;
      }
      
      // Get the event edition to check eventId
      const editionDoc = await getDoc(doc(db, 'eventEditions', editionId));
      
      if (!editionDoc.exists()) {
        console.log('[Eligibility Check] Edition not found:', editionId);
        continue;
      }
      
      const edition = editionDoc.data();
      
      // Derive eventId from edition ID if not explicitly set
      // e.g., "kutc-2026" → "kutc", "mo-2025" → "mo"
      let derivedEventId = edition.eventId;
      if (!derivedEventId && editionId) {
        const match = editionId.match(/^([a-z]+)-/i);
        if (match) {
          derivedEventId = match[1].toLowerCase();
        }
      }
      
      debugInfo.push({
        editionId,
        editionEventId: edition.eventId,
        derivedEventId,
        editionEventName: edition.eventName,
        editionNumber: edition.edition,
        targetEventId: eventId,
        matches: edition.eventId === eventId
      });
      
      // Check if this edition belongs to the same event
      // Try multiple matching strategies:
      // 1. Direct eventId match (if available)
      // 2. Derived eventId from edition ID pattern
      // 3. Case-insensitive comparison
      const editionEventId = String(derivedEventId || edition.eventId || '').toLowerCase();
      const targetEventId = String(eventId || '').toLowerCase();
      
      // Also try deriving from target if it looks like an edition ID
      let targetDerived = targetEventId;
      if (targetEventId.includes('-')) {
        const match = targetEventId.match(/^([a-z]+)-/i);
        if (match) {
          targetDerived = match[1].toLowerCase();
        }
      }
      
      console.log('[Eligibility Check] Comparing:', {
        editionId,
        editionEventId,
        targetEventId,
        targetDerived,
        match1: editionEventId === targetEventId,
        match2: editionEventId === targetDerived
      });
      
      if (editionEventId === targetEventId || editionEventId === targetDerived) {
        // User has participated in a previous edition of this event
        console.log('[Eligibility Check] ✓ MATCH FOUND for edition:', editionId);
        previousEditions.push(`${edition.eventName} ${edition.edition || ''}`);
      } else {
        console.log('[Eligibility Check] ✗ NO MATCH for edition:', editionId);
      }
    }
    
    console.log('[Eligibility Check] Debug info:', debugInfo);
    console.log('[Eligibility Check] Previous editions found:', previousEditions);
    
    if (previousEditions.length > 0) {
      return {
        eligible: true,
        previousEditions,
      };
    }
    
    return {
      eligible: false,
      reason: 'No previous participation in this event',
    };
    
  } catch (error) {
    console.error('Error checking pre-registration eligibility:', error);
    return {
      eligible: false,
      reason: 'Error checking eligibility',
    };
  }
}

/**
 * Check if pre-registration is currently active for an event edition
 * 
 * @param status - The event edition status
 * @returns boolean indicating if pre-registration is active
 */
export function isPreRegistrationActive(status: string | number): boolean {
  const statusValue = String(status || '').toLowerCase();
  const statusNumeric = parseInt(statusValue, 10);
  
  // Map string codes to their sortOrder
  const STATUS_MAP: Record<string, number> = {
    hidden: 0, draft: 10, announced: 20, pre_registration: 30, open: 40,
    waitlist: 44, late_registration: 50, full: 54, closed: 60,
    in_progress: 70, suspended: 75, finished: 80, cancelled: 90, finalized: 100
  };
  
  const eventStatusCode = !isNaN(statusNumeric) ? statusNumeric : (STATUS_MAP[statusValue] ?? 0);
  
  return eventStatusCode === 30; // pre_registration
}
